import type { SiteKey } from '@tamtamy/paperbits-common/src';
import type { CardsWallModel } from '../../cardsWallModel';
import type { QueryInput } from '../../cardsWallUtils';

export class CardsWallRuntimeModel {

    public featuredQueryInput?: QueryInput;
    public selectedQueryInput?: QueryInput;
    public staticItemsFulFilledTotal: boolean;

    constructor(
        public model: CardsWallModel,
        public queryInput: QueryInput,
        public siteKey: SiteKey,
        public siteLanguage: string,
        public preloadedItems: number,
        public precomputedItemsTotal: number
    ) {}

    public serialize(encode: boolean = false): string {
        const serial = JSON.stringify(this);
        return encode ? encodeURIComponent(serial) : serial;
    }
}

export class CardsWallRuntimeModelBuilder {
    private _model?: CardsWallModel;
    private _queryInput?: QueryInput;
    private _featuredQueryInput?: QueryInput;
    private _selectedQueryInput?: QueryInput;
    private _siteKey?: SiteKey;
    private _siteLanguage?: string;
    private _preloadedItems?: number;
    private _precomputedItemsTotal?: number;

    public model(model: CardsWallModel): CardsWallRuntimeModelBuilder {
        this._model = JSON.parse(JSON.stringify(model));
        return this;
    }

    public getModel(): CardsWallModel {
        return this._model;
    }

    public queryInput(queryInput: QueryInput): CardsWallRuntimeModelBuilder {
        this._queryInput = JSON.parse(JSON.stringify(queryInput));
        return this;
    }

    public getQueryInput(): QueryInput {
        return this._queryInput;
    }

    public featuredQueryInput(featuredQueryInput: QueryInput): CardsWallRuntimeModelBuilder {
        if (featuredQueryInput) {
            this._featuredQueryInput = JSON.parse(JSON.stringify(featuredQueryInput));
        }
        return this;
    }

    public getFeaturedQueryInput(): QueryInput {
        return this._featuredQueryInput;
    }

    public selectedQueryInput(selectedQueryInput: QueryInput): CardsWallRuntimeModelBuilder {
        if (selectedQueryInput) {
            this._selectedQueryInput = JSON.parse(JSON.stringify(selectedQueryInput));
        }
        return this;
    }

    public getSelectedQueryInput(): QueryInput {
        return this._selectedQueryInput;
    }

    public siteKey(siteKey: SiteKey): CardsWallRuntimeModelBuilder {
        this._siteKey = siteKey;
        return this;
    }

    public siteLanguage(siteLanguage: string): CardsWallRuntimeModelBuilder {
        this._siteLanguage = siteLanguage;
        return this;
    }

    public getLocale(): string {
        return this._siteKey;
    }

    public preloadedItems(preloadedItems: number): CardsWallRuntimeModelBuilder {
        this._preloadedItems = preloadedItems;
        return this;
    }

    public getPreloadedItems(): number {
        return this._preloadedItems;
    }

    public precomputedItemsTotal(precomputedItemsTotal: number): CardsWallRuntimeModelBuilder {
        this._precomputedItemsTotal = precomputedItemsTotal;
        return this;
    }

    public getPrecomputedItemsTotal(): number {
        return this._precomputedItemsTotal;
    }

    public build(): CardsWallRuntimeModel {
        const model: CardsWallRuntimeModel = new CardsWallRuntimeModel(this._model, this._queryInput, this._siteKey, this._siteLanguage, this._preloadedItems, this._precomputedItemsTotal);
        if (this._featuredQueryInput) {
            model.featuredQueryInput = this._featuredQueryInput;
        }
        if (this._selectedQueryInput) {
            model.selectedQueryInput = this._selectedQueryInput;
        }
        return model;
    }

    public static fromSerialized(serial: string, decode: boolean = false): CardsWallRuntimeModel {
        try {
            let decodedSerial = serial;
            if (decode) {
                decodedSerial = decodeURIComponent(serial);
            }
            const m: CardsWallRuntimeModel = <CardsWallRuntimeModel> JSON.parse(decodedSerial);
            if (m.queryInput) {
                if (m.queryInput.offset && typeof m.queryInput.offset === 'string') {
                    m.queryInput.offset = Number.parseInt(m.queryInput.offset);
                }
                if (m.queryInput.limit && typeof m.queryInput.limit === 'string') {
                    m.queryInput.limit = Number.parseInt(m.queryInput.limit);
                }
            }
            if (m.featuredQueryInput) {
                if (m.featuredQueryInput.offset && typeof m.featuredQueryInput.offset === 'string') {
                    m.featuredQueryInput.offset = Number.parseInt(m.featuredQueryInput.offset);
                }
                if (m.featuredQueryInput.limit && typeof m.featuredQueryInput.limit === 'string') {
                    m.featuredQueryInput.limit = Number.parseInt(m.featuredQueryInput.limit);
                }
            }
            if (m.selectedQueryInput) {
                if (m.selectedQueryInput.offset && typeof m.selectedQueryInput.offset === 'string') {
                    m.selectedQueryInput.offset = Number.parseInt(m.selectedQueryInput.offset);
                }
                if (m.selectedQueryInput.limit && typeof m.selectedQueryInput.limit === 'string') {
                    m.selectedQueryInput.limit = Number.parseInt(m.selectedQueryInput.limit);
                }
            }
            const model: CardsWallRuntimeModel = new CardsWallRuntimeModel(m.model, m.queryInput, m.siteKey, m.siteLanguage, m.preloadedItems, m.precomputedItemsTotal);
            if (m.featuredQueryInput) {
                model.featuredQueryInput = m.featuredQueryInput;
            }
            if (m.selectedQueryInput) {
                model.selectedQueryInput = m.selectedQueryInput;
            }
            return model;
        } catch {
            console.error("[Cards Wall] fromSerialized() - JSON parse exception.", serial);
            return null;
        }
    }
}